import type { FieldGroup } from '@/components/CaseSummaryView';

export interface FieldError {
  field: string;
  message?: string;
  validator?: string;
}

interface ErrorDetails {
  invalid_fields?: string[];
  missing_fields?: string[];
  field_errors?: FieldError[];
  [key: string]: any;
}

export interface ErrorWithDetails {
  message: string;
  details?: ErrorDetails;
}

/**
 * Special fields that are not part of the form schema and have custom hints
 * These are typically user profile fields that need to be set in Settings
 */
const SETTINGS_FIELDS: Record<string, { label: string; hint: string }> = {
  user_title: {
    label: 'User Title',
    hint: 'please set this in settings',
  },
};

/**
 * Creates a mapping of field IDs to their labels from input groups
 */
function createFieldLabelMap(groups: FieldGroup[]): Map<string, string> {
  const fieldMap = new Map<string, string>();

  groups.forEach(group => {
    group.fields.forEach(field => {
      fieldMap.set(field.id, field.label || field.id);
    });
  });

  return fieldMap;
}

/**
 * Formats field IDs to human-readable labels
 */
function formatFieldName(fieldId: string, labelMap: Map<string, string>): string {
  // First check if we have a label in the map
  if (labelMap.has(fieldId)) {
    return labelMap.get(fieldId)!;
  }

  // Fallback: Convert snake_case or camelCase to Title Case
  return fieldId
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Formats a list of field labels into a human-readable message
 */
function formatFieldsList(labels: string[], prefix: string, prefixPlural: string): string {
  if (labels.length === 0) {
    return '';
  }

  if (labels.length === 1) {
    return `${prefix}: ${labels[0]}`;
  }

  if (labels.length === 2) {
    return `${prefixPlural}: ${labels.join(' and ')}`;
  }

  const labelsCopy = [...labels];
  const lastField = labelsCopy.pop();
  return `${prefixPlural}: ${labelsCopy.join(', ')}, and ${lastField}`;
}

/**
 * Formats missing fields into a message
 * Handles special settings fields (like user_title) with custom hints
 */
function formatMissingFieldsMessage(
  missingFields: string[],
  labelMap: Map<string, string>,
): string | null {
  if (missingFields.length === 0) {
    return null;
  }

  // Separate settings fields from regular fields
  const settingsFields = missingFields.filter(field => field in SETTINGS_FIELDS);
  const regularFields = missingFields.filter(field => !(field in SETTINGS_FIELDS));

  // If only settings fields are missing (e.g., only user_title)
  if (regularFields.length === 0 && settingsFields.length > 0) {
    const settingsLabels = settingsFields.map(field => {
      const config = SETTINGS_FIELDS[field];
      return `${config.label} is missing, ${config.hint}`;
    });
    return settingsLabels.join('\n');
  }

  // If there are both regular and settings fields, or only regular fields
  const missingLabels = missingFields.map(field => {
    // Check if this is a settings field
    if (field in SETTINGS_FIELDS) {
      const config = SETTINGS_FIELDS[field];
      return `${config.label} (${config.hint})`;
    }
    return formatFieldName(field, labelMap);
  });

  return formatFieldsList(missingLabels, 'Missing required field', 'Missing required fields');
}

/**
 * Formats invalid fields into a message
 */
function formatInvalidFieldsMessage(
  invalidFields: string[],
  labelMap: Map<string, string>,
): string | null {
  if (invalidFields.length === 0) {
    return null;
  }

  const invalidLabels = invalidFields.map(field => formatFieldName(field, labelMap));
  return formatFieldsList(invalidLabels, 'Invalid value in field', 'Invalid values in fields');
}

/** Human-readable names for JSON-schema types in `type` validation errors */
const TYPE_DESCRIPTIONS: Record<string, string> = {
  number: 'a number',
  integer: 'a whole number',
  string: 'text',
  array: 'a list',
  boolean: 'true or false',
};

/** Maximum allowed-option values shown before truncating with an ellipsis */
const MAX_OPTIONS_SHOWN = 6;

/**
 * Whether a field error means "required but empty" — the agent's schema uses
 * minItems/minLength to enforce non-emptiness of required array/string fields,
 * so these read best as missing required fields.
 */
function isEmptyValueError(fieldError: FieldError): boolean {
  return fieldError.validator === 'minItems' || fieldError.validator === 'minLength';
}

/**
 * Extracts the allowed values from a jsonschema enum/options message like
 * `'x' is not one of ['a', 'b']`. Returns null when the message doesn't match.
 */
function parseAllowedOptions(message?: string): string | null {
  const match = message?.match(/is not one of \[(.+)\]/);
  if (!match) return null;

  const values = match[1]
    .split(', ')
    .map(value => value.replace(/^['"]|['"]$/g, ''))
    .filter(Boolean);
  if (values.length === 0) return null;

  return values.length > MAX_OPTIONS_SHOWN
    ? `${values.slice(0, MAX_OPTIONS_SHOWN).join(', ')}, …`
    : values.join(', ');
}

/**
 * Turns a nested field error into a human-readable phrase (without the field
 * label, lowercase start), e.g. "must be one of: a, b" or "must be a number".
 * Raw jsonschema messages are never echoed — unknown validators fall back to
 * a generic phrase.
 */
function humanizeFieldError(fieldError: FieldError): string {
  if (isEmptyValueError(fieldError)) {
    return 'cannot be empty';
  }
  if (fieldError.validator === 'enum' || fieldError.validator === 'options') {
    const options = parseAllowedOptions(fieldError.message);
    return options ? `must be one of: ${options}` : 'must be one of the available options';
  }
  if (fieldError.validator === 'type') {
    const match = fieldError.message?.match(/is not of type '(\w+)'/);
    const description = match ? TYPE_DESCRIPTIONS[match[1]] : undefined;
    if (description) return `must be ${description}`;
  }
  if (fieldError.validator === 'format') {
    return 'has an invalid format';
  }
  return 'has an invalid value';
}

/**
 * Standalone (capitalized) variant of the humanized field error, for display
 * next to the field itself rather than in the banner.
 */
export function humanizeFieldErrorInline(fieldError: FieldError): string {
  if (isEmptyValueError(fieldError)) return 'Value cannot be empty';

  const phrase = humanizeFieldError(fieldError);
  if (phrase === 'has an invalid value') return 'Invalid value';
  if (phrase === 'has an invalid format') return 'Invalid format';
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

/**
 * Formats nested field_errors (per-field validation errors from the PQC agent,
 * e.g. enum/format/type failures) into human-readable per-field messages.
 * Fields already reported as missing are skipped to avoid duplicate lines.
 */
function formatFieldErrorsMessage(
  fieldErrors: FieldError[],
  missingFields: string[],
  labelMap: Map<string, string>,
): string | null {
  const missingSet = new Set(missingFields);
  const messages = fieldErrors
    .filter(fe => fe.field && !missingSet.has(fe.field))
    .map(fe => `${formatFieldName(fe.field, labelMap)} ${humanizeFieldError(fe)}`);

  return messages.length > 0 ? messages.join('\n') : null;
}

/**
 * Determines if the original error message should be returned
 */
function shouldReturnOriginalMessage(error: ErrorWithDetails): boolean {
  // If no details are present
  if (!error.details || typeof error.details !== 'object') {
    return true;
  }

  const { missing_fields, invalid_fields, field_errors } = error.details;

  // If there are no missing, invalid, or per-field errors
  return !missing_fields?.length && !invalid_fields?.length && !field_errors?.length;
}

/**
 * Generates a user-friendly error message from error details
 */
export function formatErrorMessage(
  error: ErrorWithDetails | undefined,
  inputGroups: FieldGroup[] = [],
): string | null {
  if (!error) {
    return null;
  }

  // Return original message if no field-specific errors exist
  if (shouldReturnOriginalMessage(error)) {
    return error.message;
  }

  const { missing_fields = [], invalid_fields = [], field_errors = [] } = error.details!;
  const labelMap = createFieldLabelMap(inputGroups);
  const messages: string[] = [];

  // Empty-value errors (minItems/minLength) mean a required field was left
  // empty — merge them into the missing-required-fields message.
  const missingSet = new Set(missing_fields);
  const emptyValueFields = field_errors
    .filter(fe => fe.field && isEmptyValueError(fe) && !missingSet.has(fe.field))
    .map(fe => fe.field);
  const allMissingFields = [...missing_fields, ...emptyValueFields];
  const otherFieldErrors = field_errors.filter(fe => !isEmptyValueError(fe));

  // Format missing fields message
  const missingMessage = formatMissingFieldsMessage(allMissingFields, labelMap);
  if (missingMessage) {
    messages.push(missingMessage);
  }

  // Format invalid fields message
  const invalidMessage = formatInvalidFieldsMessage(invalid_fields, labelMap);
  if (invalidMessage) {
    messages.push(invalidMessage);
  }

  // Format nested per-field errors (field_errors from the PQC agent)
  const fieldErrorsMessage = formatFieldErrorsMessage(otherFieldErrors, allMissingFields, labelMap);
  if (fieldErrorsMessage) {
    messages.push(fieldErrorsMessage);
  }

  // Return formatted message
  return messages.join('\n');
}

/**
 * Groups error details by field group for better organization
 */
export function groupErrorsBySection(
  error: ErrorWithDetails | undefined,
  inputGroups: FieldGroup[],
): Map<string, string[]> {
  const errorsBySection = new Map<string, string[]>();

  if (!error?.details) {
    return errorsBySection;
  }

  const { missing_fields = [], invalid_fields = [] } = error.details;
  const allErrorFields = [
    ...missing_fields.map(f => ({ field: f, type: 'missing' })),
    ...invalid_fields.map(f => ({ field: f, type: 'invalid' })),
  ];

  inputGroups.forEach(group => {
    const groupErrors: string[] = [];

    group.fields.forEach(field => {
      const fieldError = allErrorFields.find(e => e.field === field.id);
      if (fieldError) {
        const label = field.label || formatFieldName(field.id, new Map());
        if (fieldError.type === 'missing') {
          groupErrors.push(`${label} is required`);
        } else {
          groupErrors.push(`${label} has an invalid value`);
        }
      }
    });

    if (groupErrors.length > 0) {
      errorsBySection.set(group.label, groupErrors);
    }
  });

  return errorsBySection;
}
